import Script from "next/script"
import { forwardRef, useEffect, useId, useImperativeHandle, useRef, useState } from "react"

interface TurnstileRenderOptions {
  sitekey: string
  callback: (token: string) => void
  "expired-callback"?: () => void
  "error-callback"?: (error: string) => void
}

declare global {
  interface Window {
    turnstile?: {
      render: (element: string | HTMLElement, options: TurnstileRenderOptions) => string
      reset: (widgetId: string) => void
      remove: (widgetId: string) => void
    }
  }
}

export interface TurnstileWidgetProps {
  onVerify: (token: string) => void
  onExpire?: () => void
  onError?: (error: string) => void
  className?: string
  enabled?: boolean
}

export interface TurnstileWidgetRef {
  reset: () => void
}

export const TurnstileWidget = forwardRef<TurnstileWidgetRef, TurnstileWidgetProps>(
  ({ onVerify, onExpire, onError, className, enabled = true }, ref) => {
    const containerId = useId().replace(/:/g, "-")
    const widgetIdRef = useRef<string | null>(null)
    const [scriptLoaded, setScriptLoaded] = useState(false)

    useImperativeHandle(ref, () => ({
      reset: () => {
        if (widgetIdRef.current && window.turnstile) {
          window.turnstile.reset(widgetIdRef.current)
        }
      },
    }))

    useEffect(() => {
      if (!enabled || !scriptLoaded || !window.turnstile) return

      const siteKey = process.env.NEXT_PUBLIC_TURNSTILE_SITE_KEY
      if (!siteKey) {
        console.error("[Turnstile] NEXT_PUBLIC_TURNSTILE_SITE_KEY is not defined")
        return
      }

      if (widgetIdRef.current) return

      const container = document.getElementById(containerId)
      if (!container) return

      widgetIdRef.current = window.turnstile.render(container, {
        sitekey: siteKey,
        callback: onVerify,
        "expired-callback": onExpire,
        "error-callback": onError,
      })

      return () => {
        if (widgetIdRef.current && window.turnstile) {
          window.turnstile.remove(widgetIdRef.current)
          widgetIdRef.current = null
        }
      }
    }, [enabled, scriptLoaded, containerId, onVerify, onExpire, onError])

    useEffect(() => {
      if (window.turnstile) {
        setScriptLoaded(true)
      }
    }, [])

    return (
      <>
        <Script
          id="turnstile-script"
          src="https://challenges.cloudflare.com/turnstile/v0/api.js?render=explicit"
          strategy="afterInteractive"
          onLoad={() => setScriptLoaded(true)}
        />
        <div id={containerId} className={className} />
      </>
    )
  },
)

TurnstileWidget.displayName = "TurnstileWidget"
